import type { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { isCruiseOffer, isExperienceOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import { getAllOffers } from 'selectors/offerSelectors'

function getCruiseTourV2OrExperienceOffer(
  state: App.State,
  offerId: string,
  variationId?: string,
): App.Tours.TourV2Offer | App.CruiseOffer | App.ExperienceOffer | undefined {
  const allOffers = getAllOffers(state)
  const allOffersValues = Object.values(allOffers)
  let offer: App.AnyOffer | undefined

  if (variationId) {
    const offerIdWithVariation = [offerId, variationId].join('/')

    /* Try to find the offer by its full key first, then by its id.
      Some keys don't match the offerId, like for Cruises when coming from state.offer.offers, where the key is "cruise-details:<offerId>" */
    offer = offerIdWithVariation in allOffers ?
      allOffers[offerIdWithVariation] :
        allOffersValues.find(offer => offer.id === offerIdWithVariation)

    if (offer && (isCruiseOffer(offer) || isExperienceOffer(offer) || isTourV2Offer(offer))) return offer
  }

  /* Try to find the offer by its full key first, then by its id.
    Some keys don't match the offerId, like for Cruises when coming from state.offer.offers, where the key is "cruise-details:<offerId>" */
  offer = offerId in allOffers ?
    allOffers[offerId] :
      allOffersValues.find(offer => offer.id === offerId)

  if (offer && (isCruiseOffer(offer) || isExperienceOffer(offer) || isTourV2Offer(offer))) return offer
}

type CategoryId = comluxgroup.Item_1_1_2['categoryId']
type ProductId = comluxgroup.Item_1_1_2['productId']

function getCategoryAndProductId(parentType: App.OfferParentType | 'flight', offer?: App.OrderItemOffer & { source?: string } | App.OfferBase & { source?: string }): { categoryId: CategoryId, productId: ProductId } {
  switch (parentType) {
    case 'hotel': {
      if (offer?.type === 'bedbank_hotel') return { categoryId: 'hotel', productId: 'bedbank' }
      if (offer?.type === 'tactical_ao_hotel') return { categoryId: 'hotel', productId: 'tactical_ao_hotel' }
      if (offer?.type === 'rental') return { categoryId: 'hotel', productId: 'rental' }
      if (offer?.type === 'bundle_and_save') return { categoryId: 'bundle', productId: 'bundle' }
      return { categoryId: 'hotel', productId: 'hotel' }
    }

    case 'tour_v2': return { categoryId: 'tour', productId: offer?.source === 'ttc' ? 'tourbank' : 'tour' }

    case 'tour': {
      const tourOffer = offer as App.Offer
      if (tourOffer?.holidayTypes?.includes('Cruises')) return { categoryId: 'cruise', productId: 'cruise' }
      return { categoryId: 'tour', productId: 'tour' }
    }

    case 'cruise': return { categoryId: 'cruise', productId: 'cruisebank' }

    case 'experience': {
      const experienceOffer = offer as App.ExperienceOffer
      const provider = experienceOffer.provider || getExperienceProviderFromOffer(offer?.id)
      const categoryId = 'experience'
      const productId = getExperienceProductIdFromProvider(provider)
      if (productId) return { categoryId, productId }
      return { categoryId, productId: null }
    }

    case 'car_hire': return { categoryId: 'car_hire', productId: 'car_hire' }

    case 'flight': return { categoryId: 'flight', productId: 'flight' }

    default: return { categoryId: null, productId: null }
  }
}

function getPropertyRateCategoryAndProductId(serviceType: App.BasePropertyRate['service']['type']): { categoryId: CategoryId, productId: ProductId } {
  return { categoryId: 'hotel', productId: serviceType === 'lpc' ? 'tactical_ao_hotel' : 'bedbank' }
}

function getPropertyRateOfferCategoryAndProductId(rate: App.BasePropertyRate): {
  offerId: string,
  categoryId: CategoryId,
  productId: ProductId,
} {
  return {
    offerId: rate.service.type === 'lpc' ? rate.service.offerId : rate.service.propertyId,
    ...getPropertyRateCategoryAndProductId(rate.service.type),
  }
}

function getAccommodationItemType(item: App.Checkout.BedbankHotelItem | App.Checkout.InstantBookingLEHotelItem): { categoryId: CategoryId, productId: ProductId } {
  switch (item.itemType) {
    case 'villa':
      return { categoryId: 'hotel', productId: 'rental' }
    case 'hotel':
      return { categoryId: 'hotel', productId: 'hotel' }
    case 'bedbankHotel':
      return { categoryId: 'hotel', productId: 'bedbank' }
    default:
      return { categoryId: 'hotel', productId: 'hotel' }
  }
}

function getExperienceProviderFromOffer(offerId?: string) {
  const offerProviderAndId = offerId?.split(':')
  if (offerProviderAndId && offerProviderAndId.length > 0) return offerProviderAndId[0]
}

function getExperienceProductIdFromProvider(provider?: string): ProductId {
  switch (provider) {
    case 'led': return 'led_exp'
    case 'mus': return 'mus_exp'
    case 'rez': return 'rez_exp'
    case 'dby': return 'dby_exp'
    case 'klo': return 'klo_exp'
    case 'asp': return 'asp_exp'
    case 'cln': return 'cln_exp'
    case 'cmc': return 'cmc_exp'
    // TODO: remove type assertion when contract-data-event-schemas includes tfz_exp
    case 'tfz': return 'tfz_exp' as ProductId
    default: return null
  }
}

function getHotelOrTourOffer(
  state: App.State,
  offerId: string,
  variationId?: string,
): App.Offer | App.BedbankOffer | App.OfferSummary | undefined {
  const offers = state.offer.offers
  const bedBankOffers = state.offer.bedbankOffers
  const offerSummary = state.offer.offerSummaries

  if (variationId && offers[[offerId, variationId].join('/')]) {
    return offers[[offerId, variationId].join('/')] || bedBankOffers[[offerId, variationId].join('/')]
  } else if (variationId && offerSummary[[offerId, variationId].join('/')]) {
    return offerSummary[[offerId, variationId].join('/')]
  }
  return offers[offerId] || bedBankOffers[offerId] || offerSummary[offerId]
}

function getMerchandiseOffer(
  state: App.State,
  offerId: string,
): App.MerchandiseOffer | undefined {
  return state.merchandise.merchandise[offerId]
}

function cartIsAccommodationItem(item: App.Checkout.AnyItem): item is App.Checkout.BundleAndSaveItem | App.Checkout.BedbankHotelItem | App.Checkout.InstantBookingLEHotelItem | App.Checkout.BNBLLEHotelItem | App.Checkout.VillaItem {
  return item.itemType === 'hotel' || item.itemType === 'villa' || item.itemType === 'bundleAndSave' || item.itemType === 'bedbankHotel'
}
export {
  getCruiseTourV2OrExperienceOffer,
  getCategoryAndProductId,
  getPropertyRateOfferCategoryAndProductId,
  getPropertyRateCategoryAndProductId,
  getHotelOrTourOffer,
  cartIsAccommodationItem,
  getExperienceProductIdFromProvider,
  getExperienceProviderFromOffer,
  getAccommodationItemType,
  getMerchandiseOffer,
}
